import type { ReactElement } from "react"
import styled from "styled-components"

type Fit = "cover" | "contain"

interface RemoteImageProps {
  readonly src: string | undefined
  readonly alt: string
  /** The well's aspect ratio, width over height. */
  readonly ratio: number
  /**
   * `cover` only where every image the caller can be handed matches `ratio` —
   * cropping art that does not fit takes the edge of a cover, which is where its
   * title is. `contain` mats it onto whatever is painted behind instead.
   */
  readonly fit?: Fit
  readonly qaHook?: string
}

const Figure = styled.div<{ $ratio: number }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $ratio }) => $ratio};
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.md};
`

const Image = styled.img<{ $fit: Fit }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $fit }) => $fit};
`

/**
 * Artwork served from the CDN at runtime. vite-imagetools cannot pre-process
 * these — it only sees local imports — so the win here is avoiding layout shift
 * and deferring offscreen work. Local assets should use an imagetools import
 * instead. See FINDINGS.md.
 */
export const RemoteImage = ({ src, alt, ratio, fit = "contain", qaHook }: RemoteImageProps): ReactElement => (
  <Figure $ratio={ratio} data-qa-hook={qaHook}>
    {src ? <Image src={src} alt={alt} $fit={fit} loading="lazy" decoding="async" /> : null}
  </Figure>
)
